<script setup lang="ts">
import moment from "moment";
import { computed, ref } from "vue";

import { getFieldNames } from "./getNames";
import CaseSearchFieldDescription from "./CaseSearchFieldDescription.vue";

import { DateValidation } from "@/models/DateValidation";

import type { DateRangeFilterField } from "@/models/Column";

withDefaults(defineProps<{
  config: DateRangeFilterField;
  fieldPath: string;
  displayName: string;
  isCollection: boolean;
  lastSearch?: { value?: { from?: string; to?: string } };
}>(), {  })

const fromDate = ref(__props.lastSearch?.value?.from ?? "");
const differentDays = ref(__props.lastSearch?.value?.to !== __props.lastSearch?.value?.from);
const toDate = ref(differentDays.value ? (__props.lastSearch?.value?.to ?? "") : "");

const { fieldTypeName, fieldToValueName, fieldFromValueName, fieldIsCollectionName, fieldHelpTextId } = getFieldNames(
  __props.fieldPath,
  __props.config,
);

const absoluteMax = moment(new Date(9999, 11, 31), "YYYY-MM-DD", true);
const toMinMaxAttr = (moment: moment.Moment) => moment.toISOString().slice(0, 10);

const minimumDate = computed(() =>
  toMinMaxAttr(
    moment.max(
      DateValidation.minimumDate,
      fromDate.value ? moment(fromDate.value, "YYYY-MM-DD", true) : DateValidation.minimumDate,
    ),
  ),
);

const maximumDate = computed(() =>
  toMinMaxAttr(
    moment.min(absoluteMax, moment.max(toDate.value ? moment(toDate.value, "YYYY-MM-DD", true) : absoluteMax)),
  ),
);

function setRange(timestamp: number) {
  const getDate = (date: Date) => date.toISOString().slice(0, 10);

  fromDate.value = getDate(new Date(Date.now() - timestamp));
  toDate.value = getDate(new Date());

  differentDays.value = true;
}

function isDateRange(state: boolean) {
  differentDays.value = state;
  toDate.value = "";
}
</script>

<template>
  <div class="max-w-md tw-relative tw-shrink-0 tw-space-y-1">
    <label
      tabindex="0"
      :for="`${!differentDays ? fieldFromValueName : ''}`"
      class="tw-block tw-text-sm tw-font-medium tw-underline-offset-2 tw-outline-none focus:tw-underline"
    >
      {{ displayName }}
    </label>
    <CaseSearchFieldDescription v-if="config.helpText" :id="fieldHelpTextId" :help-text="config.helpText" />
    <div v-if="config.allowRange !== false" class="tw-flex tw-items-center tw-justify-start tw-gap-2 tw-text-sm">
      <input
        :id="`${fieldPath}_differentDay`"
        type="checkbox"
        class="tw-border tw-border-gray-500 tw-bg-white"
        :checked="differentDays"
        :aria-label="`Toggle to ${
          differentDays ? 'disable' : 'enable'
        } a date range selection on the ${displayName} field`"
        @change="(e) => isDateRange((e.target as HTMLInputElement).checked)"
      />
      <label :for="`${fieldPath}_differentDay`">Date range</label>
    </div>
    <div
      :aria-label="config.description"
      :aria-describedby="config.helpText ? fieldHelpTextId : undefined"
      class="tw-relative tw-flex tw-w-full tw-flex-wrap tw-gap-1"
    >
      <input type="hidden" :name="fieldTypeName" value="date-range" />
      <input type="hidden" :name="fieldIsCollectionName" :value="isCollection" />
      <div class="tw-flex tw-flex-1 tw-items-center tw-gap-2">
        <label v-if="differentDays" :for="fieldFromValueName" class="tw-text-xs">From</label>
        <input
          :id="fieldFromValueName"
          v-model="fromDate"
          type="date"
          :min="toMinMaxAttr(DateValidation.minimumDate)"
          :max="maximumDate"
          :name="fieldFromValueName"
          :aria-label="differentDays ? 'From Date' : 'Date Selection'"
          :aria-describedby="config.helpText ? fieldHelpTextId : undefined"
          class="invalid:tw-is-input-invalid tw-w-full tw-rounded-md tw-border tw-border-gray-500 tw-bg-white tw-p-2"
        />
      </div>
      <div v-if="differentDays" class="tw-flex tw-flex-1 tw-items-center tw-gap-2">
        <label :for="fieldToValueName" class="tw-text-xs">To</label>
        <input
          :id="fieldToValueName"
          v-model="toDate"
          type="date"
          :min="minimumDate"
          :max="toMinMaxAttr(absoluteMax)"
          aria-label="To Date"
          :name="fieldToValueName"
          :aria-describedby="config.helpText ? fieldHelpTextId : undefined"
          class="invalid:tw-is-input-invalid tw-w-full tw-rounded-md tw-border tw-border-gray-500 tw-bg-white tw-p-2"
        />
      </div>
      <input v-else type="hidden" :value="fromDate" :name="fieldToValueName" />
    </div>
    <ul
      v-if="Object.keys(config.options ?? {}).length > 0"
      aria-label="Date Range Options"
      class="tw-mt-2 tw-flex tw-flex-wrap tw-items-center tw-justify-start tw-gap-x-2 tw-gap-y-1"
    >
      <li v-for="{ name, diff } in config.options" :key="name" :aria-label="`Click to set range from now to ${name}`">
        <button type="button" class="tw-btn-md-gray tw-min-w-max" @click="() => setRange(diff)">
          {{ name }}
        </button>
      </li>
    </ul>
  </div>
</template>
